/**
 * Full-page create / edit user (Platform → Users)
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Switch,
  Checkbox,
  Button,
  Typography,
  Space,
  Row,
  Col,
  Alert,
  Modal,
  Spin,
  message,
  Upload,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  ArrowLeftOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  UploadOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { resolvePublicAssetUrl } from '@/config/apiBaseUrl';
import { useNavigate, useParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import userService, { User as ApiUser } from '@/services/user.service';
import roleService, { Role } from '@/services/role.service';
import companyService from '@/services/company.service';
import { useAuthStore } from '@/store/authStore';
import type { Company, User } from '@/types';
import authService from '@/services/auth.service';
import { mergeUserClaimsFromToken } from '@/utils/jwtPermissions';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';

const { Title, Text } = Typography;

function isUserLocked(u: { locked_until?: string | null } | null | undefined): boolean {
  if (!u?.locked_until) return false;
  return dayjs(u.locked_until).isAfter(dayjs());
}

export const UserFormPage: React.FC<{ mode: 'create' | 'edit' }> = ({ mode }) => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(mode === 'edit');
  const [roles, setRoles] = useState<Role[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>();
  const [signaturePreview, setSignaturePreview] = useState<string | undefined>();
  const [editUser, setEditUser] = useState<ApiUser | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const isSuperAdmin = !!authUser?.is_super_admin;
  const isEdit = mode === 'edit';

  const syncAuthProfileIfSelf = (updates: Partial<User>) => {
    if (!authUser?.id || !userId || authUser.id !== userId) return;
    const token = authService.getToken();
    const next = { ...authUser, ...updates } as User;
    const merged = token ? mergeUserClaimsFromToken(next, token) : next;
    useAuthStore.getState().setUser(merged);
  };

  const roleOptions = useMemo(
    () =>
      roles.map((r) => ({
        value: r.id,
        label: `${r.name} (${r.code})`,
      })),
    [roles]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await roleService.getRoles({
          is_active: true,
          company_id: authUser?.company_id || undefined,
        });
        if (!cancelled) setRoles(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setRoles([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authUser?.company_id]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await companyService.listCompanies();
        if (!cancelled) setCompanies(list);
      } catch {
        if (!cancelled) setCompanies([]);
        message.warning('Could not load companies (company.view required)');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isEdit || !userId) return;
    let cancelled = false;
    (async () => {
      setPageLoading(true);
      try {
        const u = await userService.getById(userId);
        const ur = await userService.getUserRoles(userId);
        if (cancelled) return;
        setEditUser(u);
        setAvatarPreview(u.avatar_url);
        setSignaturePreview(u.signature_image_url);
        form.setFieldsValue({
          email: u.email,
          first_name: u.first_name,
          last_name: u.last_name,
          phone: u.phone,
          designation: u.designation,
          default_department_id: u.default_department_id ?? undefined,
          is_active: u.is_active,
          role_ids: ur.map((x) => x.role_id),
          company_ids:
            (u as any).company_ids?.length
              ? (u as any).company_ids
              : (u as any).memberships?.length
                ? [
                    ...((u as any).memberships as { company_id: string; is_parent: boolean }[])
                      .sort((a, b) => Number(b.is_parent) - Number(a.is_parent))
                      .map((m) => m.company_id),
                  ]
                : u.company_id
                  ? [u.company_id]
                  : [],
        });
      } catch {
        message.error('Failed to load user');
        navigate('/platform/security/users');
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, userId, navigate]);

  const handleUnlock = async () => {
    if (!userId) return;
    setUnlocking(true);
    try {
      const updated = await userService.unlock(userId);
      setEditUser(updated);
      message.success('Account unlocked');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Unlock failed');
    } finally {
      setUnlocking(false);
    }
  };

  useEffect(() => {
    if (isEdit) return;
    form.setFieldsValue({
      send_invite: false,
      password_must_change: false,
      role_ids: [],
    });
  }, [isEdit, form]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      if (isEdit && userId) {
        await userService.update(userId, {
          email: values.email,
          first_name: values.first_name,
          last_name: values.last_name,
          phone: values.phone,
          designation: values.designation,
          default_department_id: values.default_department_id ?? null,
          is_active: values.is_active,
          ...(isSuperAdmin && values.company_ids?.length
            ? { company_ids: values.company_ids }
            : {}),
        });
        await userService.assignRoles(userId, values.role_ids || []);
        syncAuthProfileIfSelf({
          first_name: values.first_name,
          last_name: values.last_name,
          phone: values.phone,
          email: values.email,
          designation: values.designation,
          default_department_id: values.default_department_id ?? null,
        });
        message.success('User updated');
        message.info('The user must sign out and sign in again for menu and permission changes to take effect.');
        navigate('/platform/security/users');
        return;
      }

      const companyIds: string[] | undefined = isSuperAdmin
        ? values.company_ids?.length
          ? values.company_ids
          : values.company_id
            ? [values.company_id]
            : undefined
        : undefined;

      const res = await userService.create({
        username: values.username,
        email: values.email,
        first_name: values.first_name,
        last_name: values.last_name,
        designation: values.designation,
        default_department_id: values.default_department_id ?? null,
        phone: values.phone,
        password: values.send_invite ? undefined : values.password,
        send_invite: !!values.send_invite,
        password_must_change: !!values.password_must_change,
        company_id: companyIds?.[0],
        company_ids: companyIds,
        role_ids: values.role_ids || [],
      });
      if (res.inviteEmailSent) {
        message.success('User created; invitation email sent');
      } else if (res.passwordSetupLink) {
        message.warning('Copy setup link for the user (email failed)');
        Modal.info({
          title: 'Password setup link',
          content: (
            <Input.TextArea autoSize value={res.passwordSetupLink} readOnly />
          ),
        });
      } else {
        message.success('User created');
      }
      if ((values.role_ids || []).length > 0) {
        message.info('The user must sign out and sign in again for menu and permission changes to take effect.');
      }
      navigate('/platform/security/users');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 720 }}>
      <SmartBreadcrumbs />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/platform/security/users')}
          style={{ padding: 0 }}
        >
          Back to users
        </Button>

        <Title level={3}>
          <UserOutlined /> {isEdit ? 'Edit user' : 'Create user'}
        </Title>

        {isEdit && isUserLocked(editUser) && (
          <Alert
            type="warning"
            showIcon
            message="Account temporarily locked"
            description={
              <Space direction="vertical" size="small">
                <Text>
                  Locked until {dayjs(editUser!.locked_until).format('DD MMM YYYY HH:mm')} due to failed
                  login attempts.
                </Text>
                <Button
                  type="primary"
                  icon={<UnlockOutlined />}
                  loading={unlocking}
                  onClick={handleUnlock}
                >
                  Unlock account
                </Button>
              </Space>
            }
          />
        )}

        {!isEdit && (
          <Alert
            type="info"
            showIcon
            message="Roles and permissions"
            description={
              <Space direction="vertical" size="small">
                <Text>
                  Create or clone a role, then assign permissions on the{' '}
                  <strong>Roles</strong> page. Pick those roles below.
                </Text>
                <Space wrap>
                  <Link to="/platform/security/roles">
                    <Button type="primary" icon={<TeamOutlined />} size="small">
                      Manage roles
                    </Button>
                  </Link>
                  <Link to="/platform/security/permissions">
                    <Button icon={<SafetyCertificateOutlined />} size="small">
                      Permissions overview
                    </Button>
                  </Link>
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Effective permission codes are edited per role on Manage roles.
                </Text>
                <Text type="warning" style={{ fontSize: 12 }}>
                  A role named &quot;Super Admin&quot; does <strong>not</strong> automatically grant full access.
                  Menus follow permission codes on each role, or full access only if a platform super admin sets{' '}
                  <Text code>is_super_admin</Text> on the user (then re-login). Troubleshooting:{' '}
                  <Text code>docs/SUPER_ADMIN_ACCESS.md</Text>.
                </Text>
              </Space>
            }
          />
        )}

        {isEdit && (
          <Alert
            type="warning"
            showIcon
            message="Role name vs full access"
            description={
              <Text style={{ fontSize: 12 }}>
                Assigning a role whose label is &quot;Super Admin&quot; only applies whatever permissions that role has on{' '}
                <strong>Roles</strong>. It does not set platform super admin. To widen menus without changing the platform
                flag, edit the role and add permission codes; users must sign in again for a new token. For troubleshooting
                SQL and steps, see <Text code>docs/SUPER_ADMIN_ACCESS.md</Text> in the repo.
              </Text>
            }
          />
        )}

        <Card>
          <Form form={form} layout="vertical" onFinish={onFinish}>
            {!isEdit && (
              <Form.Item
                name="username"
                label="Username"
                rules={[{ required: true, min: 3 }]}
              >
                <Input prefix={<UserOutlined />} />
              </Form.Item>
            )}
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, type: 'email' }]}
            >
              <Input prefix={<MailOutlined />} />
            </Form.Item>
            <Form.Item
              name="first_name"
              label="First name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="last_name" label="Last name">
              <Input />
            </Form.Item>
            <Form.Item name="phone" label="Phone">
              <Input />
            </Form.Item>
            <Form.Item name="designation" label="Designation">
              <Input placeholder="Job title (shown on approval prints)" />
            </Form.Item>
            <Form.Item
              name="default_department_id"
              label="Default department"
              extra="Used to prefill department on new documents when you are in this company. Does not change HR employee department or approval Process Owner."
            >
              <DepartmentSelect allowClear placeholder="Optional default department" />
            </Form.Item>

            {isEdit && userId && (
              <>
                <Form.Item label="Profile photo">
                  <Space align="start" wrap>
                    {avatarPreview ? (
                      <img
                        src={resolvePublicAssetUrl(avatarPreview) || ''}
                        alt=""
                        style={{
                          width: 72,
                          height: 72,
                          objectFit: 'cover',
                          borderRadius: 6,
                          border: '1px solid #f0f0f0',
                        }}
                      />
                    ) : null}
                    <Upload
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      showUploadList={false}
                      customRequest={async ({ file, onSuccess, onError }) => {
                        try {
                          const res = await userService.uploadAvatar(userId, file as File);
                          setAvatarPreview(res.avatar_url);
                          syncAuthProfileIfSelf({ avatar_url: res.avatar_url });
                          message.success('Profile photo updated');
                          onSuccess?.(res, file as any);
                        } catch (e: any) {
                          message.error(e?.response?.data?.message || 'Upload failed');
                          onError?.(e);
                        }
                      }}
                    >
                      <Button icon={<UploadOutlined />}>Upload photo</Button>
                    </Upload>
                  </Space>
                </Form.Item>
                <Form.Item label="Digital signature">
                  <Space align="start" direction="vertical" size="small">
                    {signaturePreview ? (
                      <img
                        src={resolvePublicAssetUrl(signaturePreview) || ''}
                        alt=""
                        style={{ maxHeight: 56, maxWidth: 220, objectFit: 'contain' }}
                      />
                    ) : null}
                    <Upload
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      showUploadList={false}
                      customRequest={async ({ file, onSuccess, onError }) => {
                        try {
                          const res = await userService.uploadSignature(userId, file as File);
                          setSignaturePreview(res.signature_image_url);
                          syncAuthProfileIfSelf({ signature_image_url: res.signature_image_url });
                          message.success('Signature image updated');
                          onSuccess?.(res, file as any);
                        } catch (e: any) {
                          message.error(e?.response?.data?.message || 'Upload failed');
                          onError?.(e);
                        }
                      }}
                    >
                      <Button icon={<UploadOutlined />}>Upload signature</Button>
                    </Upload>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Used on printed documents after universal approval.
                    </Text>
                  </Space>
                </Form.Item>
              </>
            )}

            {isSuperAdmin && (
              <Form.Item
                name="company_ids"
                label="Companies"
                extra="First selected company is the home (parent). Roles are mirrored to the others."
                rules={[{ required: !isEdit, message: 'Select at least one company' }]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Select companies (first = home)"
                  optionFilterProp="label"
                  options={companies.map((c) => ({
                    value: c.id,
                    label: `${c.name} (${c.code})`,
                  }))}
                  loading={companies.length === 0}
                  notFoundContent={companies.length ? undefined : 'No companies loaded'}
                />
              </Form.Item>
            )}

            {!isEdit && (
              <>
                <Form.Item name="send_invite" valuePropName="checked">
                  <Checkbox>Send invite email (password setup link)</Checkbox>
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(p, c) => p.send_invite !== c.send_invite}>
                  {({ getFieldValue }) =>
                    !getFieldValue('send_invite') ? (
                      <>
                        <Form.Item
                          name="password"
                          label="Password"
                          rules={[
                            { required: true, message: 'Required unless invite' },
                            { min: 10, message: 'Min 10 characters' },
                          ]}
                        >
                          <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Upper, lower, digit"
                          />
                        </Form.Item>
                        <Form.Item
                          name="password_must_change"
                          valuePropName="checked"
                        >
                          <Checkbox>User must change password on first login</Checkbox>
                        </Form.Item>
                      </>
                    ) : null
                  }
                </Form.Item>
              </>
            )}

            <Form.Item
              name="role_ids"
              label="Roles"
              rules={[{ required: true, message: 'Select at least one role' }]}
            >
              <Select
                mode="multiple"
                options={roleOptions}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            {isEdit && (
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            )}

            <Row gutter={16}>
              <Col>
                <Button onClick={() => navigate('/platform/security/users')}>
                  Cancel
                </Button>
              </Col>
              <Col>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {isEdit ? 'Save changes' : 'Create user'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>
      </Space>
    </div>
  );
};

export const UserCreatePage: React.FC = () => <UserFormPage mode="create" />;
export const UserEditPage: React.FC = () => <UserFormPage mode="edit" />;

export default UserCreatePage;
